// ContextWarpers.js
import React, { createContext, useContext, useState } from 'react';

// Map to store all created contexts
export const contextRegistry = {}; // Assurez-vous que le contextRegistry est exporté

/**
 * Creates a named context for use in JSX
 * @param {string} name - Unique name for the context
 * @param {any} defaultValue - Default value for the context
 * @returns {React.Context} - The created context
 */
export const createNamedContext = (name, defaultValue) => {
  if (!contextRegistry[name]) {
    const context = createContext(defaultValue);
    contextRegistry[name] = { context, defaultValue };
  }
  return contextRegistry[name].context;
};

/**
 * Provider component for a named context
 * @param {Object} props
 * @param {string} props.name - Name of the context
 * @param {any} props.value - Value to provide to the context
 * @param {React.ReactNode} props.children - Child components
 */
export const ContextProvider = ({ name, value, children }) => {
  if (!name) {
    console.error('Context name is required');
    return null;
  }

  // Get or create the context
  const Context = createNamedContext(name, null);
  
  return (
    <Context.Provider value={value}>
      {children}
    </Context.Provider>
  );
};

/**
 * Consumer component for a named context
 * @param {Object} props
 * @param {string} props.name - Name of the context to consume
 * @param {Function} props.children - Render function that receives context value
 */
export const ContextConsumer = ({ name, children }) => {
  if (!name || !contextRegistry[name]) {
    console.error(`Context "${name}" is not registered`);
    return children(null);
  }
  
  const { context } = contextRegistry[name];
  
  return (
    <context.Consumer>
      {value => children(value)}
    </context.Consumer>
  );
};

/**
 * Hook-based wrapper for consuming a named context
 * @param {Object} props
 * @param {string} props.name - Name of the context to consume
 * @param {Function} props.children - Render function that receives context value
 */
export const UseContext = ({ name, children }) => {
  // Always call useContext hook, regardless of whether the context exists
  // This ensures the hook is called in the same order every time
  const contextEntry = name ? contextRegistry[name] : null;
  const Context = contextEntry ? contextEntry.context : createContext(null);
  const value = useContext(Context);
  
  // After the hook is called, we can add our conditional logic
  if (!name || !contextRegistry[name]) {
    console.error(`Context "${name}" is not registered`);
    return children(null);
  }
  
  return children(value);
};

/**
 * Provides a named context with state (combines context with useState)
 * @param {Object} props
 * @param {string} props.name - Name for the context
 * @param {any} props.initialValue - Initial state value
 * @param {React.ReactNode} props.children - Child components
 */
export const StateContextProvider = ({ name, initialValue, children }) => {
  const [state, setState] = useState(initialValue);
  
  return (
    <ContextProvider name={name} value={[state, setState]}>
      {children}
    </ContextProvider>
  );
};

export default {
  createNamedContext,
  ContextProvider,
  ContextConsumer,
  UseContext,
  StateContextProvider,
  contextRegistry // Assurez-vous que le contextRegistry est inclus ici aussi
};